import { wrapWithEventPropertiesPrefix } from '@adsk/exo-skeleton-api/internal';

import { EventContext } from '../monitoring';
import { getDTMonitoringContext } from '../monitoringContext';
import { getNavigationTiming } from '../navigationTiming';

interface DynatraceEvent {
  scope?: string;
  context?: EventContext;
}

//This function is used to spread the scope property value to higher level since dynatrace doesn't support nested object
export function spreadScopedPropertiesForRootLevel(context: EventContext) {
  if (!context.scope) {
    return context;
  }

  const scope = context.scope;

  // We add custom property with scope as a key in report api - https://git.autodesk.com/BIM360/acs-sc-web-platform/blob/master/libs/exo-skeleton-api/monitoring.ts#L77
  // We need to spread the scope property value to higher level since dynatrace doesn't support nested object
  const dynatraceContext: EventContext = {
    ...context,
    ...(context?.[scope] || {}),
  };

  // We need to remove scope field from context to avoid duplication once they will support objects (currently doesn't impact anything)
  if (scope && context.hasOwnProperty(scope)) {
    delete dynatraceContext[scope]; //:TODO: Remove scope field after spread value, currently don't work
  }

  return dynatraceContext;
}

// Pay attention that this is a global filter. It is not case-sensitive and uses contains logic
const FILTERED_ERROR_MESSAGES = [
  'Pending user action for user consent preferences',
  'ResizeObserver loop completed with undelivered notifications',
].map((message) => message.toLowerCase());

function isFilteredErrorMessage(message: unknown): boolean {
  if (typeof message !== 'string' || !message) {
    return false;
  }

  const lowerCaseMessage = message.toLowerCase();
  return FILTERED_ERROR_MESSAGES.some((filteredMessage) =>
    lowerCaseMessage.includes(filteredMessage),
  );
}

function shouldFilterOutException(event: Record<string, unknown>): boolean {
  return isFilteredErrorMessage(event?.['exception.message']);
}

type DtrumReportError = (
  this: unknown,
  error: unknown,
  parentActionId?: number,
) => void;

interface DtrumAPI {
  reportError?: DtrumReportError;
}

declare global {
  interface Window {
    dtrum?: DtrumAPI;
  }
  var dtrum: DtrumAPI | undefined;
}

function getReportedErrorMessage(error: unknown): unknown {
  if (typeof error === 'string') {
    return error;
  }
  return (error as { message?: unknown } | null)?.message;
}

const filteredReportErrors = new WeakSet<DtrumReportError>();

// Non-function values are returned as is, so the SDK sees the same value it would without the filter.
function filterReportError(
  reportError: DtrumReportError | undefined,
): DtrumReportError | undefined {
  if (
    typeof reportError !== 'function' ||
    filteredReportErrors.has(reportError)
  ) {
    return reportError;
  }

  const filtered: DtrumReportError = function (this: unknown, error, ...rest) {
    if (isFilteredErrorMessage(getReportedErrorMessage(error))) {
      return;
    }
    reportError.call(this, error, ...rest);
  };
  filteredReportErrors.add(filtered);
  return filtered;
}

const patchedDtrumObjects = new WeakSet<DtrumAPI>();

// The agent may reassign `reportError` after registration, so the setter filters every new value.
function patchDtrum(dtrum: DtrumAPI): void {
  if (patchedDtrumObjects.has(dtrum)) {
    return;
  }

  let reportError = filterReportError(dtrum.reportError);
  Object.defineProperty(dtrum, 'reportError', {
    configurable: true,
    enumerable: true,
    get: () => reportError,
    set: (next: DtrumReportError | undefined) => {
      reportError = filterReportError(next);
    },
  });
  patchedDtrumObjects.add(dtrum);
}

let windowDtrum: DtrumAPI | undefined;

function setWindowDtrum(dtrum: DtrumAPI | undefined): void {
  windowDtrum = dtrum;
  if (dtrum) {
    // This runs inside the agent's `window.dtrum = ...` assignment, so it must never throw.
    try {
      patchDtrum(dtrum);
    } catch (e) {
      console.error(`Failed to patch dtrum.reportError: ${e}`);
    }
  }
}

// The agent may replace the whole `window.dtrum` object, so its setter patches the new object too.
function watchWindowDtrum(): void {
  if (
    Object.getOwnPropertyDescriptor(globalThis, 'dtrum')?.set !== setWindowDtrum
  ) {
    windowDtrum = globalThis.dtrum;
    Object.defineProperty(globalThis, 'dtrum', {
      configurable: true,
      enumerable: true,
      get: () => windowDtrum,
      set: setWindowDtrum,
    });
  }

  if (windowDtrum) {
    patchDtrum(windowDtrum);
  }
}

// Errors reported through the classic `dtrum.reportError` API (e.g. by the ADP analytics SDK bundled
// in remotes: "Sending event returns error. Error: Pending user action for user consent preferences")
// don't go through `addEventModifier`, so we apply the same message filter at the API entry point.
// The SDK reads `globalThis.dtrum` on every report, so callers never hold an unwrapped reference.
// Returns false when `dtrum.reportError` isn't available yet, so the caller can retry.
export function filterDtrumReportError(): boolean {
  if (typeof globalThis.dtrum?.reportError !== 'function') {
    return false;
  }

  try {
    watchWindowDtrum();
    return true;
  } catch (e) {
    console.error(`Failed to patch dtrum.reportError: ${e}`);
    return false;
  }
}

export const addEventModifier = (
  event: Record<string, unknown>,
): Record<string, unknown> | null => {
  try {
    if (shouldFilterOutException(event)) {
      return null;
    }

    const result = {
      ...event,
      ...wrapWithEventPropertiesPrefix({
        ...getDTMonitoringContext(),
        ...getNavigationTiming(),
      }),
    };

    return result;
  } catch (e) {
    console.error(`Failed to use dynatrace addEventModifier: ${e}`);
    return event;
  }
};

export function sendEventToDynatrace({ scope, context = {} }: DynatraceEvent) {
  try {
    if (!window.dynatrace) {
      return;
    }

    const dynatraceContext: EventContext =
      spreadScopedPropertiesForRootLevel(context);

    window.dynatrace.sendEvent({
      ...wrapWithEventPropertiesPrefix({ scope }),
      ...dynatraceContext,
    });
  } catch (e) {
    console.error(`Failed to send event to Dynatrace: ${e}`);
  }
}
